// FILE: src/components/QuestionFactory/GenerationForm.js
//
// Admin form to kick off a generation batch.
// Creates a pipeline_runs row, then calls runGeneration which dispatches
// to the chosen provider adapter.
//
// Gap-targeted generation (hard restriction):
//   The factory only generates into UNDERSTOCKED gaps. On mount we load the
//   live active-question inventory and compare it against each cert's
//   blueprint target (src/config/bankTargets.js). Certs and categories that
//   are at/above target are disabled, and the form refuses to submit unless
//   the chosen cert + category are both still understocked. This keeps
//   generation effort focused where the bank is thin (today: CPMAI) instead
//   of topping up exams that are already well stocked.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createPipelineRun,
  runGeneration,
  listAvailableProviders,
  getActiveInventory,
} from '../../services/questionFactoryService';
import { rankCertGaps } from '../../config/bankTargets';

const EXAM_LABELS = {
  pmp:       'PMP',
  'pmi-acp': 'PMI-ACP',
  cpmai:     'CPMAI',
};

const DIFFICULTIES = ['Easy', 'Medium', 'Hard'];

const STYLES = {
  card: {
    background: '#15152a',
    border: '1px solid #2a2a4a',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '24px',
  },
  title: { fontSize: '1rem', fontWeight: 600, color: '#eee', marginBottom: '12px' },
  row: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gap: '12px',
    marginBottom: '12px',
  },
  label: { fontSize: '0.7rem', color: '#aab', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '4px' },
  input: {
    width: '100%', background: '#0f0f1e', border: '1px solid #3a3a5e', borderRadius: '4px',
    color: '#eee', padding: '8px 10px', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: '0.9rem',
  },
  textarea: {
    width: '100%', background: '#0f0f1e', border: '1px solid #3a3a5e', borderRadius: '4px',
    color: '#eee', padding: '8px 10px', boxSizing: 'border-box', fontFamily: 'inherit',
    fontSize: '0.85rem', minHeight: '60px', resize: 'vertical',
  },
  button: {
    padding: '10px 18px', background: '#3a3a6e', border: '1px solid #5a5a9e', borderRadius: '6px',
    color: '#fff', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600,
  },
  disabled: { opacity: 0.5, cursor: 'not-allowed' },
  error:    { color: '#ff6b6b', fontSize: '0.85rem', marginTop: '8px' },
  success:  { color: '#7be88c', fontSize: '0.85rem', marginTop: '8px' },
  hint:     { fontSize: '0.7rem', color: '#888', marginTop: '4px' },
  gapPanel: {
    background: '#0f0f1e', border: '1px solid #2a2a4a', borderRadius: '6px',
    padding: '12px 14px', marginBottom: '16px', fontSize: '0.8rem', color: '#ccd',
  },
  gapRow: { display: 'flex', justifyContent: 'space-between', padding: '2px 0' },
  gapGood: { color: '#7be88c' },
  gapWarn: { color: '#ffd479' },
  note:    { fontSize: '0.8rem', color: '#aab', marginTop: '6px' },
};

export default function GenerationForm({ onRunCreated }) {
  // Only providers that are actually invocable from the browser show up
  // in the picker. CLI-only providers surface their status in the Providers
  // table further down.
  const providers = useMemo(
    () => listAvailableProviders().filter(
      (p) => p.enabled && !p.cliOnly && p.supportedModes.includes('generate'),
    ),
    [],
  );

  const [gaps,        setGaps]        = useState([]);   // ranked CertGap[]
  const [invLoading,  setInvLoading]  = useState(true);
  const [invError,    setInvError]    = useState(null);

  const [examId,    setExamId]    = useState('');
  const [provider,  setProvider]  = useState(providers[0]?.id || '');
  const [count,     setCount]     = useState(5);
  const [category,  setCategory]  = useState('');
  const [difficulty,setDifficulty]= useState('Medium');
  const [extra,     setExtra]     = useState('');
  const [busy,      setBusy]      = useState(false);
  const [error,     setError]     = useState(null);
  const [result,    setResult]    = useState(null);

  // ── Load live inventory and compute gaps on mount ──────────────────────
  const loadInventory = useCallback(async () => {
    setInvLoading(true);
    setInvError(null);
    try {
      const inventory = await getActiveInventory();
      const ranked = rankCertGaps(inventory);
      setGaps(ranked);
      // Default to the most understocked cert + its biggest category gap.
      const firstOpen = ranked.find((g) => g.understocked);
      if (firstOpen) {
        setExamId((prev) => prev || firstOpen.examId);
      }
    } catch (err) {
      setInvError(err.message || String(err));
    } finally {
      setInvLoading(false);
    }
  }, []);

  useEffect(() => { loadInventory(); }, [loadInventory]);

  const currentGap = useMemo(
    () => gaps.find((g) => g.examId === examId) || null,
    [gaps, examId],
  );

  // Understocked categories for the selected cert (the only valid targets).
  const openCategories = useMemo(
    () => (currentGap ? currentGap.categories.filter((c) => c.understocked) : []),
    [currentGap],
  );

  // When the cert changes (or first loads), snap the category to the
  // biggest open gap so the default is always a valid target.
  useEffect(() => {
    if (!currentGap) return;
    const ranked = [...openCategories].sort((a, b) => b.gap - a.gap);
    setCategory((prev) => {
      const stillOpen = ranked.some((c) => c.category === prev);
      return stillOpen ? prev : (ranked[0]?.category || '');
    });
  }, [currentGap, openCategories]);

  const anyUnderstocked = gaps.some((g) => g.understocked);
  const selectedCatGap  = openCategories.find((c) => c.category === category) || null;
  const canSubmit =
    !busy && !invLoading && !!provider &&
    !!currentGap && currentGap.understocked &&
    !!selectedCatGap;

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const run = await createPipelineRun({
        examId,
        source:         provider,
        domain:         category || null,
        requestedCount: Number(count),
        providerModel:  null,
        options: {
          difficulty: difficulty || null,
          extra_instructions: extra || null,
          target_category: category || null,
          content_gap: true,
        },
      });
      const gen = await runGeneration({
        runId:             run.id,
        provider,
        examId,
        count:             Number(count),
        domain:            category || null,
        difficulty:        difficulty || null,
        extraInstructions: extra || null,
      });
      setResult({ runId: run.id, ...gen });
      if (typeof onRunCreated === 'function') onRunCreated(run.id);
      // Refresh inventory so the gap picture (and gating) reflects the new draws.
      loadInventory();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  }, [canSubmit, examId, provider, count, category, difficulty, extra, onRunCreated, loadInventory]);

  if (providers.length === 0) {
    return (
      <div style={STYLES.card}>
        <div style={STYLES.title}>Generate Batch</div>
        <div style={STYLES.error}>No providers are enabled yet. Check the provider API-key secrets on this Supabase project.</div>
      </div>
    );
  }

  return (
    <form style={STYLES.card} onSubmit={submit} data-testid="qf-generation-form">
      <div style={STYLES.title}>Generate Batch · Content Gaps Only</div>

      {invLoading && (
        <div style={STYLES.note} data-testid="qf-inventory-loading">Loading bank inventory…</div>
      )}
      {invError && (
        <div style={STYLES.error} data-testid="qf-inventory-error">
          Couldn’t load bank inventory: {invError}
        </div>
      )}

      {!invLoading && !invError && !anyUnderstocked && (
        <div style={STYLES.note} data-testid="qf-no-gaps">
          Every certification bank is at or above its stocking target — there are no content gaps to fill right now.
        </div>
      )}

      {!invLoading && !invError && anyUnderstocked && (
        <>
          <div style={STYLES.gapPanel} data-testid="qf-gap-panel">
            <div style={{ marginBottom: '6px', color: '#eee', fontWeight: 600 }}>Bank stocking vs target</div>
            {gaps.map((g) => (
              <div key={g.examId} style={STYLES.gapRow}>
                <span>{EXAM_LABELS[g.examId] || g.examId}</span>
                <span style={g.understocked ? STYLES.gapWarn : STYLES.gapGood}>
                  {g.activeTotal} / {g.targetTotal}
                  {g.understocked ? ` · gap ${g.totalGap}` : ' · stocked ✓'}
                </span>
              </div>
            ))}
          </div>

          <div style={STYLES.row}>
            <div>
              <label style={STYLES.label} htmlFor="qf-cert">Certification (understocked only)</label>
              <select id="qf-cert" data-testid="qf-cert-select" style={STYLES.input}
                      value={examId} onChange={(e) => setExamId(e.target.value)}>
                {gaps.map((g) => (
                  <option key={g.examId} value={g.examId} disabled={!g.understocked}>
                    {EXAM_LABELS[g.examId] || g.examId}
                    {g.understocked ? ` — gap ${g.totalGap}` : ' — stocked (blocked)'}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={STYLES.label} htmlFor="qf-category">Category (gap)</label>
              <select id="qf-category" data-testid="qf-category-select" style={STYLES.input}
                      value={category} onChange={(e) => setCategory(e.target.value)}>
                {currentGap && currentGap.categories.map((c) => (
                  <option key={c.category} value={c.category} disabled={!c.understocked}>
                    {c.category} ({c.active}/{c.target})
                    {c.understocked ? ` — need ${c.gap}` : ' — stocked'}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={STYLES.label} htmlFor="qf-provider">Provider</label>
              <select id="qf-provider" data-testid="qf-provider-select" style={STYLES.input}
                      value={provider} onChange={(e) => setProvider(e.target.value)}>
                {providers.map((p) => <option key={p.id} value={p.id}>{p.displayName}</option>)}
              </select>
            </div>
            <div>
              <label style={STYLES.label} htmlFor="qf-count">Count (1–5)</label>
              <input id="qf-count" data-testid="qf-count" type="number" min={1} max={5} style={STYLES.input}
                     value={count} onChange={(e) => setCount(e.target.value)} />
              <div style={STYLES.hint}>Cap per run — adjudication runs inline.</div>
            </div>
            <div>
              <label style={STYLES.label} htmlFor="qf-difficulty">Difficulty</label>
              <select id="qf-difficulty" data-testid="qf-difficulty" style={STYLES.input}
                      value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
                <option value="">(any)</option>
                {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label style={STYLES.label} htmlFor="qf-extra">Extra instructions (optional)</label>
            <textarea id="qf-extra" data-testid="qf-extra" style={STYLES.textarea}
                      placeholder="e.g. emphasise scenario-based stems under hybrid delivery"
                      value={extra} onChange={(e) => setExtra(e.target.value)} />
          </div>

          <div style={{ marginTop: '12px' }}>
            <button type="submit" data-testid="qf-generate-submit"
                    style={canSubmit ? STYLES.button : { ...STYLES.button, ...STYLES.disabled }}
                    disabled={!canSubmit}>
              {busy
                ? 'Generating…'
                : `Generate ${count} ${(EXAM_LABELS[examId] || examId || '').toString()} · ${category || 'pick a gap'}`}
            </button>
            {currentGap && !currentGap.understocked && (
              <div style={STYLES.error} data-testid="qf-blocked-stocked">
                {EXAM_LABELS[examId] || examId} is already stocked — pick an understocked certification.
              </div>
            )}
          </div>
        </>
      )}

      {error && <div style={STYLES.error} data-testid="qf-generate-error">{error}</div>}
      {result && (
        <div style={STYLES.success} data-testid="qf-generate-success">
          Inserted {result.inserted} draft{result.inserted === 1 ? '' : 's'} into run {result.runId.slice(0, 8)}…
          {result.failed > 0 && ` (${result.failed} failed)`}
          {result.modelUsed && ` · model: ${result.modelUsed}`}
          {result.inputTokens > 0 && ` · ${result.inputTokens} in / ${result.outputTokens} out tokens`}
          {result.estimatedCostUsd > 0 && ` · est. cost: $${result.estimatedCostUsd.toFixed(4)}`}
        </div>
      )}
    </form>
  );
}
